import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "../components/Layout";
import { Header } from "../components/header";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

export const HomePage = ({ data }) => (
  <Layout contactData={data.Contact}>
    {/* ── Hero ── */}
    <Header data={data.Header} />

    {/* ── Why Choose Transwave ── */}
    <div className="services-detail-section">
      <div className="container">
        <div className="section-title text-center">
          <h2>Why Choose Transwave</h2>
          <p>Built on trust, compliance, and professional logistics expertise across every transport mode.</p>
        </div>
        <div className="row">
          {data.Features?.map((f, i) => (
            <motion.div
              key={i}
              className="col-md-3 col-sm-6"
              {...fadeUp(0.08 + i * 0.08)}
            >
              <div className="services-detail-card">
                <i className={f.icon}></i>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>

    {/* ── About Preview ── */}
    <section id="about" className="hp-about-section">
      <div className="container">
        <div className="row hp-about-row">
          <motion.div className="col-xs-12 col-md-5 hp-about-img-col" {...fadeUp(0)}>
            <div className="hp-about-img-wrap">
              <img src="img/about.jpg" className="img-responsive" alt="Transwave Shipping operations" />
              <div className="hp-about-img-badge">
                <span className="hp-about-badge-val">3+</span>
                <span className="hp-about-badge-lbl">Years of<br/>Excellence</span>
              </div>
            </div>
          </motion.div>
          <motion.div className="col-xs-12 col-md-7" {...fadeUp(0.15)}>
            <div className="hp-about-text">
              <span className="hp-about-tag"><i className="fa fa-certificate"></i> UAE Licensed &amp; Regulated</span>
              <h2>About Us</h2>
              <p>{data.About?.paragraph?.split("\n\n")[0]}</p>
              <div className="hp-about-highlights">
                <div className="hp-about-highlight">
                  <i className="fa fa-check-circle"></i>
                  <div>
                    <strong>{data.About?.Mission?.title}</strong>
                    <span>{data.About?.Mission?.text}</span>
                  </div>
                </div>
                <div className="hp-about-highlight">
                  <i className="fa fa-check-circle"></i>
                  <div>
                    <strong>{data.About?.Vision?.title}</strong>
                    <span>{data.About?.Vision?.text}</span>
                  </div>
                </div>
              </div>
              <Link to="/about" className="btn btn-custom">Learn More About Us</Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ── Stats Strip ── */}
    <motion.div className="hp-stats-strip" {...fadeUp(0)}>
      <div className="container">
        <div className="row hp-stats-row">
          {[
            { val: "UAE", label: "Licensed & Regulated" },
            { val: "3+",    label: "Years Active" },
            { val: "24h",   label: "Quote Response" },
            { val: "100%",  label: "Compliance" },
          ].map((s, i) => (
            <div key={i} className="col-xs-6 col-sm-3 hp-stat">
              <span className="hp-stat-value">{s.val}</span>
              <span className="hp-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>

    {/* ── Services Preview ── */}
    <section id="hp-services">
      <div className="container">
        <div className="section-title text-center">
          <h2>Our Services</h2>
          <p>Shipping, freight forwarding, and logistics solutions — built for global markets.</p>
        </div>
        <div className="row hp-services-cards">
          {data.Services?.map((s, i) => (
            <motion.div key={i} className="col-md-4 col-sm-6 hp-service-card-col" {...fadeUp(0.05 * i)}>
              <div className="hp-service-card">
                <div className="hp-service-card-icon">
                  <i className={s.icon}></i>
                </div>
                <div className="hp-service-card-body">
                  <h3>{s.name}</h3>
                  <p>{s.text}</p>
                  <Link to="/services" className="hp-service-card-link">
                    Learn more <i className="fa fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp(0.3)} style={{ marginTop: 36, textAlign: "center" }}>
          <Link to="/services" className="btn btn-custom">View All Services</Link>
        </motion.div>
      </div>
    </section>



    {/* ── Gallery Preview — first 4 images ── */}
    <section id="gallery" className="text-center">
      <div className="container">
        <div className="section-title">
          <h2>Our Operations</h2>
          <p>A glimpse into our cargo handling and logistics work.</p>
        </div>
        <div className="row">
          {data.Gallery?.slice(0, 4).map((g, i) => (
            <motion.div key={i} className="col-xs-6 col-sm-3" {...fadeUp(0.06 * i)}>
              <div className="hp-gallery-thumb">
                <img src={g.smallImage} alt={g.title} className="img-responsive" />
                <div className="hp-gallery-overlay"><span>{g.title}</span></div>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp(0.3)} style={{ marginTop: 24 }}>
          <Link to="/gallery" className="btn btn-custom">View Full Gallery</Link>
        </motion.div>
      </div>
    </section>

    {/* ── Compliance Trust Strip ── */}
    <motion.div className="hp-compliance-strip" {...fadeUp(0)}>
      <div className="container">
        <div className="hp-compliance-inner">
          <i className="fa fa-certificate hp-cert-icon"></i>
          <div className="hp-compliance-text">
            <strong>Fully Licensed & UAE Compliant</strong>
            <p>All activities comply with UAE regulatory standards for freight forwarding, cargo handling, and logistics.</p>
          </div>
          <Link to="/compliance" className="btn btn-custom hp-compliance-btn">View Compliance</Link>
        </div>
      </div>
    </motion.div>

    {/* ── Contact CTA ── */}
    <section id="hp-contact" className="hp-contact-cta">
      <div className="container">
        <div className="hp-cta-split">
          <motion.div className="hp-cta-text" {...fadeUp(0)}>
            <h2>Get a Free Shipping Quote</h2>
            <p>Tell us about your shipment — sea, air, or road — and we'll respond within 24 hours.</p>
            <Link to="/contact" className="btn btn-outline-cta hp-cta-link">Full Contact Form &rarr;</Link>
          </motion.div>
          <motion.div className="hp-cta-actions" {...fadeUp(0.15)}>
            {data.Contact?.phone && (
              <a href={`tel:${data.Contact.phone.replace(/\s/g, "")}`} className="hp-cta-card">
                <i className="fa fa-phone"></i>
                <div>
                  <span className="hp-cta-card-label">Call Us</span>
                  <span className="hp-cta-card-value">{data.Contact.phone}</span>
                </div>
              </a>
            )}
            {data.Contact?.whatsapp && (
              <a href={data.Contact.whatsapp} target="_blank" rel="noopener noreferrer" className="hp-cta-card hp-cta-card--wa">
                <i className="fa fa-whatsapp"></i>
                <div>
                  <span className="hp-cta-card-label">WhatsApp</span>
                  <span className="hp-cta-card-value">Chat instantly</span>
                </div>
              </a>
            )}
            {data.Contact?.email && (
              <a href={`mailto:${data.Contact.email}`} className="hp-cta-card">
                <i className="fa fa-envelope-o"></i>
                <div>
                  <span className="hp-cta-card-label">Email Us</span>
                  <span className="hp-cta-card-value">{data.Contact.email}</span>
                </div>
              </a>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  </Layout>
);

